import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { JournalEntryType } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { cloudVersionsOf, createOwnRowSync, marksOf, mergeLoad, ownCache, rowFocusRefresh, type OwnRowSync, type RowMarks } from '../lib/ownRowSync';
import { refreshBlobOnFocus } from '../lib/blobFocusRefresh';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';

// ─── Supabase sync helpers (sync epic — same pattern as task/habit/event) ───
// journal_entries keeps a few typed columns + a `data` jsonb holding the full
// JournalEntryType (the read source of truth). A load keeps an unsaved edit of the
// entry as the cloud still has it — no clock decides (lib/ownRowSync.ts). Writes are
// fire-and-forget.
// ⚠️⚠️ A DELETED ENTRY STAYS DELETED (2026-10-18): writes go through `lib/ownRowSync.ts` —
// only entries created here and unconfirmed (`unsyncedIds`) are inserted, every other write
// is an UPDATE, and an entry the cloud no longer has is dropped here, never re-created.

const JOURNAL_TABLE = 'journal_entries';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Only real (uuid) entries sync — the old built-in sample entries used ids '1'..'7', which a uuid column rejects. */
const isSynced = (e: JournalEntryType | undefined): e is JournalEntryType => !!e && UUID_RE.test(e.id);

/** The row's columns, without its id (an UPDATE never sets the id). */
function entryFields(entry: JournalEntryType, userId: string) {
  return {
    user_id: userId,
    focus_level: entry.focusLevel ?? 0,
    created_at: entry.createdAt,
    updated_at: entry.updatedAt,
    data: entry,
  };
}

function rowToEntry(row: any): JournalEntryType {
  const data = row?.data;
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    return data as JournalEntryType;
  }
  // Stub-table fallback (no title/content columns); shouldn't happen for rows
  // we write since they always carry `data`.
  return {
    id: row.id,
    focusLevel: row.focus_level ?? 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  } as JournalEntryType;
}

const journalSync: OwnRowSync<JournalEntryType> = createOwnRowSync<JournalEntryType>({
  table: JOURNAL_TABLE,
  label: 'journal',
  fields: entryFields,
  current: (id) => useJournalStore.getState().entries.find((e) => e.id === id),
  fromRow: rowToEntry,
  replace: (entry) => useJournalStore.setState((s) => ({ entries: s.entries.map((e) => (e.id === entry.id ? entry : e)) })),
  unsyncedIds: () => useJournalStore.getState().unsyncedIds,
  confirm: (id) => useJournalStore.setState((s) =>
    s.unsyncedIds.includes(id) ? { unsyncedIds: s.unsyncedIds.filter((x) => x !== id) } : s),
  drop: (id) => useJournalStore.setState((s) => ({
    entries: s.entries.filter((e) => e.id !== id),
    unsyncedIds: s.unsyncedIds.filter((x) => x !== id),
  })),
  marks: () => useJournalStore.getState(),
  setMarks: (marks) => useJournalStore.setState(marks),
  name: (e) => e.title || e.date || e.id,
});

/** Save these entries (each through its own queue — see `lib/ownRowSync.ts`). */
const cloudSave = (entries: JournalEntryType | JournalEntryType[]): Promise<boolean> =>
  journalSync.save((Array.isArray(entries) ? entries : [entries]).filter(isSynced));

async function cloudDelete(id: string): Promise<void> {
  if (!id || !UUID_RE.test(id)) return;
  // ⚠️ After any save of this entry already on the wire: a delete that overtook the entry's
  // own insert would be undone by it.
  await journalSync.settle(id);
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase.from(JOURNAL_TABLE).delete().eq('id', id);
    if (error) console.error('Failed to delete journal entry from Supabase:', error);
  } catch (error) {
    console.error('Error deleting journal entry from Supabase:', error);
  }
}

interface JournalState extends RowMarks<JournalEntryType> {
  entries: JournalEntryType[];
  isLoading: boolean;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** Entries created on THIS device whose insert the cloud hasn't confirmed (see lib/ownRowSync.ts). Persisted. */
  unsyncedIds: string[];
  // `dirtyIds` + `cloudVersions` + `dirtyBases` (RowMarks, persisted): what a load decides by, without a clock — lib/ownRowSync.ts.
  initialize: () => Promise<void>;

  // CRUD operations
  addEntry: (entry: Omit<JournalEntryType, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateEntry: (id: string, updates: Partial<JournalEntryType>) => void;
  deleteEntry: (id: string) => void;

  // Getters
  getEntryById: (id: string) => JournalEntryType | undefined;
  getEntriesByDate: (date: string) => JournalEntryType[];
  getEntriesByDateRange: (startDate: string, endDate: string) => JournalEntryType[];

  // Energy response settings
  energyResponseSettings: {
    enabled: boolean;
    customActions: Record<string, Array<{
      type: string;
      id: string;
      name: string;
      description: string;
    }>>;
  };
  updateEnergyResponseSettings: (settings: Partial<JournalState['energyResponseSettings']>) => void;
}

// Sample entries for initial state
/**
 * ⚠️⚠️ THE STORE SHIPS SEEDED DEMO ENTRIES, AND THEY NEVER GO AWAY.
 *
 * `initialize` merges cloud rows over local ones BY ID as a union, and these ids
 * ('1'…'7') exist in no user's cloud, so they are never overwritten and never
 * removed. Every account therefore holds these entries permanently.
 *
 * Consequence for anything ASKING A QUESTION of this store: `entries.length > 0`
 * is true for a user who has never written anything. Count
 * `selectAuthoredEntries` instead — the Growth Module's "have you journalled?"
 * step read the raw length first and completed itself on day one.
 */
/**
 * ⚠️⚠️ THE SEED IS GONE (2026-09-01) BUT ITS ROWS ARE NOT. Seven demo entries
 * with these ids shipped as the initial state and were PERSISTED into every
 * existing install's `journal-storage`, so deleting the array only helps people
 * who have never opened the app. The `migrate` below strips them at load, and
 * this set is what it uses.
 *
 * ⚠️ Do not "simplify" these to numbers. Ids are STRINGS, and a real entry whose
 * id merely begins with a seeded digit — `"71"` — must not be mistaken for one.
 */
const SAMPLE_ENTRY_IDS = new Set(['1', '2', '3', '4', '5', '6', '7']);

/**
 * Entries the USER actually wrote.
 *
 * ⭐ KEPT AFTER THE SEED WAS DELETED, deliberately. The migration cleans stored
 * state once, at load; this is what guarantees no surface ever counts or shows
 * a demo row however that state arrived — the guarantee that was missing when
 * `JournalWorkspace` displayed all seven to a brand-new account.
 */
export const selectAuthoredEntries = (entries: JournalEntryType[]): JournalEntryType[] =>
  entries.filter(e => !SAMPLE_ENTRY_IDS.has(e.id));


/**
 * ⚠️ EXPLICIT PERSIST GENERICS, LIKE `useCurrentView` AND `onboardingStore`.
 * Adding `migrate` to the options changes what zustand has to infer, and bare
 * `persist(...)` then gives up on the whole state type — every `set` callback
 * below turns into `any` and the file still "compiles" until `noImplicitAny`
 * catches it. Naming the types keeps that from happening silently.
 */
export const useJournalStore = create<JournalState>()(
  persist<JournalState, [], [], Pick<JournalState, 'entries' | 'energyResponseSettings' | 'cachedUserId' | 'unsyncedIds' | 'dirtyIds' | 'cloudVersions'>>(
    (set, get) => ({
      entries: [],
      isLoading: false,
      initializedUserId: null,
      cachedUserId: null,
      unsyncedIds: [],
      dirtyIds: [],
      cloudVersions: {},
      dirtyBases: {},

      // Load-on-login, keyed by user id (re-runs on account switch). Merge the
      // current user's local cache with the cloud: the cloud's copy wins except an
      // unsaved edit of the entry (merged onto it if written since); a different account's
      // cache is replaced (cloud authoritative). A cached entry the cloud didn't
      // return was deleted elsewhere and goes, unless it is unsynced or never syncs
      // (lib/ownRowSync.ts mergeLoad).
      initialize: () => dedupeInit('journal_entries', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('journal_entries', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { result: { data, error }, win } = await journalSync.load(() => supabase
            .from(JOURNAL_TABLE)
            .select('*'));
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const cloudEntries = (data ?? []).map(rowToEntry);

          let kept: string[] = [];
          set((state) => {
            const merged = mergeLoad({
              ...ownCache(state.cachedUserId, user.id, state.entries, state.unsyncedIds),
              marks: marksOf(state),
              cloud: cloudEntries,
              versions: cloudVersionsOf(data ?? []),
              win,
              keep: (e) => !isSynced(e), // never in the cloud by design: not a ghost
              label: 'journal',
            });
            kept = merged.kept.filter((id) => UUID_RE.test(id)); // a sample entry never syncs
            return {
              entries: merged.rows,
              unsyncedIds: merged.unsyncedIds.filter((id) => UUID_RE.test(id)),
              ...merged.marks,
              initializedUserId: user.id,
              cachedUserId: user.id,
            };
          });
          journalSync.seen(cloudEntries, win); // the base of each entry's next edit (lib/ownRowSync.ts)
          void journalSync.writeBack(kept); // what the merge kept over the cloud (unsynced, or an unsaved edit)
          markCloudFresh('journal_entries', user.id);
        } catch (error) {
          console.error('Failed to initialize journal from Supabase:', error);
        }
      }),

      // Energy response settings
      energyResponseSettings: {
        enabled: true,
        customActions: {}
      },
      
      updateEnergyResponseSettings: (settings) => {
        set(state => ({
          energyResponseSettings: {
            ...state.energyResponseSettings,
            ...settings
          }
        }));
      },
      
      addEntry: (entryData) => {
        const newEntry: JournalEntryType = {
          id: crypto.randomUUID(),
          ...entryData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        set(state => ({
          entries: [newEntry, ...state.entries],
          unsyncedIds: [...state.unsyncedIds, newEntry.id], // ← INSERTED until the cloud confirms it
        }));

        void cloudSave(newEntry);
      },

      updateEntry: (id, updates) => {
        set(state => ({
          entries: state.entries.map(entry =>
            entry.id === id
              ? {
                  ...entry,
                  ...updates,
                  updatedAt: new Date().toISOString()
                }
              : entry
          )
        }));

        const updated = get().entries.find(e => e.id === id);
        if (updated) void cloudSave(updated);
      },

      deleteEntry: (id) => {
        journalSync.noteDeleted(id);
        set(state => ({
          entries: state.entries.filter(entry => entry.id !== id),
          unsyncedIds: state.unsyncedIds.filter((x) => x !== id),
        }));

        void cloudDelete(id);
      },
      
      getEntryById: (id) => {
        return get().entries.find(entry => entry.id === id);
      },
      
      getEntriesByDate: (date) => {
        return get().entries.filter(entry => entry.date === date);
      },
      
      getEntriesByDateRange: (startDate, endDate) => {
        return get().entries.filter(entry => {
          const entryDate = new Date(entry.date);
          const start = new Date(startDate);
          const end = new Date(endDate);
          
          return entryDate >= start && entryDate <= end;
        });
      }
    }),
    {
      name: 'journal-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      /**
       * ⚠️⚠️ BUMPED WHEN THE DEMO SEED WAS DELETED. Removing `sampleEntries`
       * from the initial state helps only people who have never opened the app.
       * Everyone else already has the seven rows written to disk, where they
       * would sit for ever. This strips them once, at load.
       *
       * ⚠️ It filters by id and touches nothing else. Someone who EDITED a demo
       * entry loses that edit — the correct trade, since the alternative is
       * keeping a fabricated reflection because it was modified.
       */
      version: 2,
      migrate: (persisted, from) => {
        const prior = persisted as JournalState | undefined;
        if (from < 2 && prior && Array.isArray(prior.entries)) {
          return {
            ...prior,
            // ⭐ The same function every display surface uses, so "what counts
            // as a demo row" is defined in exactly one place.
            entries: selectAuthoredEntries(prior.entries),
          };
        }
        return persisted as JournalState;
      },
      // `initializedUserId`/`isLoading` stay transient.
      partialize: (state) => ({
        entries: state.entries,
        energyResponseSettings: state.energyResponseSettings,
        cachedUserId: state.cachedUserId,
        // ⚠️ A cache from before this field has none: all cloud copies (ghosts go on load).
        unsyncedIds: state.unsyncedIds,
        // ⚠️ A cache from before these has no marks: no unsaved edit, so its first load takes the cloud's copies.
        dirtyIds: state.dirtyIds,
        cloudVersions: state.cloudVersions,
        // ⚠️ Each unsaved row's base: without it a load drops that edit (lib/ownRowSync.ts). A cache from
        // before it has none.
        dirtyBases: state.dirtyBases,
      }),
      // The saved copy read back (a page load, a change of account): the base of each entry's next
      // edit — as this device last saw it in the cloud; an unsaved one's, its saved base (lib/ownRowSync.ts
      // `restore`). ⚠️ Runs inside `create`, before `useJournalStore` exists: the marks come with `state`.
      onRehydrateStorage: () => (state) => { if (state) journalSync.restore(state.entries, state); },
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account read the last
   one's journal until its own load; entries that live only here (never synced, or made offline) were
   replaced by the next account's load — gone for good; and the next account's entries written before
   its own load were kept by the last one's. */
scopeToAccount(useJournalStore);

// ⭐ A tab that comes back shows the entries the cloud holds now — read only (lib/ownRowSync.ts `rowFocusRefresh`).
refreshBlobOnFocus(JOURNAL_TABLE, rowFocusRefresh(journalSync, {
  label: 'journal',
  loadedFor: () => useJournalStore.getState().initializedUserId,
  held: () => useJournalStore.getState().entries,
  versions: () => supabase.from(JOURNAL_TABLE).select('id, updated_at'),
  keep: (e) => !isSynced(e), // never in the cloud by design: not a ghost
  apply: (read) => useJournalStore.setState((state) => {
    const merged = mergeLoad({
      cached: state.entries, unsyncedIds: state.unsyncedIds, marks: marksOf(state), ...read.over(state.entries),
      keep: (e) => !isSynced(e), label: 'journal',
    });
    return { entries: merged.rows, unsyncedIds: merged.unsyncedIds, ...merged.marks };
  }),
}));
